import { WarningCircleIcon } from "@phosphor-icons/react";
import { type Control, Controller } from "react-hook-form";

import { Input, Tooltip } from "@alpacahq/ui/old";

import { ToggleOptions } from "~/v2/components/common/ToggleOptions";
import type { TrailType } from "~/v2/constants/equityOrder";
import type { TrailingStopPrice } from "../hooks/useTrailingStopPrice";
import type { EquityOrderTicketSchema } from "../schema";

/**
 * @todo - Update this component show buy and sell stop prices
 */

const BUTTON_RATES = [1, 2, 3];

const TRAIL_TYPE_OPTIONS: { label: string; value: TrailType }[] = [
  {
    label: "$",
    value: "trail_price",
  },
  {
    label: "%",
    value: "trail_percent",
  },
];

interface TrailingValueInputProps {
  control: Control<EquityOrderTicketSchema>;
  trailType: TrailType;
  onTrailTypeChange: (trailType: TrailType) => void;
  trailingStopPrice: TrailingStopPrice;
}

export const TrailingValueInput = ({
  control,
  trailType,
  onTrailTypeChange,
  trailingStopPrice,
}: TrailingValueInputProps) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-1">
      <label
        htmlFor="trailValue"
        className="font-semibold text-gray-700 text-sm"
      >
        {trailType === "trail_price" ? "Trailing Price" : "Trailing Rate"}
      </label>
      <ToggleOptions
        ariaLabel="Trail Type"
        options={TRAIL_TYPE_OPTIONS}
        value={trailType}
        onChange={onTrailTypeChange}
        size="xs"
      />
    </div>
    <Controller
      name="trailValue"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          {trailType === "trail_price" ? (
            <Input
              id="trailValue"
              type="number"
              min="0"
              step="any"
              leftIcon={
                <span className="font-medium font-mono text-gray-700 text-xs">
                  $
                </span>
              }
              {...field}
              errorMsg={error?.message}
            />
          ) : (
            <div className="relative">
              <Input
                id="trailValue"
                type="number"
                min="0"
                step="any"
                rightIcon={
                  <span className="font-medium text-gray-700 text-xs">%</span>
                }
                {...field}
                errorMsg={error?.message}
              />
              <div className="absolute top-0 right-6 translate-y-[calc(50%-2px)]">
                <div className="flex items-center justify-end gap-1">
                  {BUTTON_RATES.map((rate) => (
                    <button
                      key={rate}
                      type="button"
                      onClick={(_event) => field.onChange(rate)}
                      className="flex h-5 w-8 items-center justify-center rounded border border-blue-600 bg-[rgba(191,219,254,0.24)] font-medium text-blue-600 text-xs transition-colors hover:border-blue-700 hover:text-blue-700 active:border-blue-500 active:text-blue-500"
                    >
                      {rate}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    />
    <div className="mt-1 flex items-start justify-between gap-1">
      <p className="m-0 flex items-center justify-start gap-1 p-0 text-gray-700 text-xs">
        Stop Price{" "}
        <Tooltip
          content="This is the same as stop price in the regular stop/stop limit orders, but this is derived from the high water mark (HWM) value and trail parameter, and continuously updates as HWM changes."
          placement="top"
        >
          <WarningCircleIcon size={16} />
        </Tooltip>
      </p>
      <div className="flex flex-col gap-1">
        <p className="m-0 flex items-center justify-end gap-2 p-0 text-xs">
          <span className="text-gray-700">Buy</span>{" "}
          <span className="font-medium text-gray-950">
            {trailingStopPrice.buy}
          </span>
        </p>
        <p className="m-0 flex items-center justify-end gap-2 p-0 text-xs">
          <span className="text-gray-700">Sell</span>{" "}
          <span className="font-medium text-gray-950">
            {trailingStopPrice.sell}
          </span>
        </p>
      </div>
    </div>
  </div>
);
